// ========================================
// pages/AdminDashboard.js - Admin Control Panel
// ========================================
// PURPOSE: Main admin dashboard with multiple tabs
// - Overview: System statistics and recent activity
// - Users: Manage all users with search and pagination
// - Unclaimed Assets: View unclaimed assets registry
// - Integrity Alerts: View and manage tampering alerts
// - Plan Config: Edit plan limits and pricing
// - PIN Management: Manage user PINs
// - Reports: Generate system reports
// - Audit Logs: View login attempts and actions
// - Admins: Manage other admin accounts (super_admin only)
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import IntegrityAlerts from '../components/admin/IntegrityAlerts';
import PlanConfig from '../components/admin/PlanConfig';
import PinManagement from '../components/admin/PinManagement';
import Reports from '../components/admin/Reports';
import AuditLogs from '../components/admin/AuditLogs';
import AdminManagement from '../components/admin/AdminManagement';
import AdminSecurity from '../components/admin/AdminSecurity';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [unclaimed, setUnclaimed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [admin, setAdmin] = useState(null);
  const [integrityCount, setIntegrityCount] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Search and pagination for users
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  
  // Unclaimed assets modal state
  const [selectedUnclaimed, setSelectedUnclaimed] = useState(null);
  const [showUnclaimedModal, setShowUnclaimedModal] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(false);

  // Get token from localStorage
  const getToken = () => localStorage.getItem('adminToken');

  // ========================================
  // CHECK AUTHENTICATION ON MOUNT
  // ========================================
  useEffect(() => {
    const token = getToken();
    const adminStr = localStorage.getItem('admin');
    
    if (!token || !adminStr) {
      navigate('/admin/login');
      return;
    }
    
    setAdmin(JSON.parse(adminStr));
    fetchDashboardData();
    fetchIntegrityCount();
  }, []);

  // ========================================
  // FETCH MAIN DASHBOARD DATA
  // ========================================
  const fetchDashboardData = async () => {
    try {
      const token = getToken();
      const [statsRes, usersRes, unclaimedRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/unclaimed`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setUnclaimed(unclaimedRes.data.assets || []);
    } catch (err) {
      setError('Failed to load dashboard data');
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FETCH INTEGRITY ALERT COUNT
  // ========================================
  const fetchIntegrityCount = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/integrity/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIntegrityCount(response.data.open);
    } catch (err) {
      console.error('Failed to fetch integrity count:', err);
    }
  };

  // ========================================
  // CHANGE PASSWORD
  // ========================================
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      alert('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const token = getToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/change-password`, {
        currentPassword,
        newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // ========================================
  // VIEW UNCLAIMED ASSET DETAILS
  // ========================================
  const viewUnclaimedDetails = async (unclaimedId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/unclaimed/${unclaimedId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedUnclaimed(response.data);
      setShowUnclaimedModal(true);
    } catch (err) {
      alert('Failed to load unclaimed asset details');
    }
  };

  // ========================================
  // DOWNLOAD SUPPORTING DOCUMENT
  // ========================================
  const downloadDocument = async (assetSnapshot) => {
    if (!assetSnapshot?.document?.fileUrl) {
      alert('No supporting document available for this asset');
      return;
    }
    
    setDownloadingDoc(true);
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${assetSnapshot.originalAssetId}/document`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: assetSnapshot.document.fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', assetSnapshot.document.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document');
    } finally {
      setDownloadingDoc(false);
    }
  };

  // ========================================
  // FORMAT DATE
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ========================================
  // LOGOUT WITH CONFIRMATION
  // ========================================
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    navigate('/admin/login');
  };

  // ========================================
  // FILTER USERS BY SEARCH TERM
  // ========================================
  const filteredUsers = users.filter(user =>
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ========================================
  // PAGINATION
  // ========================================
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // ========================================
  // SKELETON LOADER
  // ========================================
  const SkeletonLoader = () => (
    <div className="admin-skeleton">
      <div className="admin-skeleton-header"></div>
      <div className="admin-skeleton-card"></div>
      <div className="admin-skeleton-card"></div>
      <div className="admin-skeleton-card"></div>
    </div>
  );

  // ========================================
  // RENDER LOADING STATE WITH SKELETON
  // ========================================
  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-inner">
            <div className="admin-logo-area">
              <img src="/images/symbols/golden-stool.png" alt="Agyapadie" className="admin-logo-icon" />
              <span className="admin-logo-text">AGYAPADIE ADMIN</span>
            </div>
          </div>
        </div>
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* ======================================== */}
      {/* HEADER SECTION with Avatar Dropdown */}
      {/* ======================================== */}
      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-logo-area">
            <img 
              src="/images/symbols/logo-light.png" 
              alt="Agyapadie" 
              className="admin-logo-icon"
            />
          </div>
          <div className="admin-header-right">
            <div className="admin-profile" onClick={() => setAdminMenuOpen(!adminMenuOpen)}>
              <img src="/images/profile/user-avatar.png" alt="Admin" className="admin-avatar" />
              <span className="admin-name">{admin?.fullName?.split(' ')[0]}</span>
              <span className="admin-arrow">▼</span>
              {adminMenuOpen && (
                <div className="admin-dropdown">
                  <button onClick={() => { setAdminMenuOpen(false); setShowChangePassword(true); }}>
                    🔑 Change Password
                  </button>
                  <button onClick={() => { setAdminMenuOpen(false); setShowLogoutConfirm(true); }}>
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🚪</div>
            <h3 className="modal-title">Confirm Logout</h3>
            <p className="modal-text">Are you sure you want to logout from Admin Dashboard?</p>
            <div className="modal-buttons">
              <button onClick={() => setShowLogoutConfirm(false)} className="modal-cancel-btn">
                Cancel
              </button>
              <button onClick={handleLogout} className="modal-confirm-btn">
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🔑</div>
            <h3 className="modal-title">Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <small className="form-hint">Minimum 6 characters</small>
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="modal-buttons" style={{ marginTop: '25px' }}>
                <button type="button" onClick={() => setShowChangePassword(false)} className="modal-cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="modal-confirm-btn" disabled={changingPassword}>
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* TAB NAVIGATION */}
      {/* ======================================== */}
      <div className="admin-tabs">
        <button onClick={() => setActiveTab('overview')} className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}>📊 Overview</button>
        <button onClick={() => setActiveTab('users')} className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}>👥 Users</button>
        <button onClick={() => setActiveTab('unclaimed')} className={`admin-tab ${activeTab === 'unclaimed' ? 'active' : ''}`}>🏦 Unclaimed Assets</button>
        <button onClick={() => setActiveTab('integrity')} className={`admin-tab ${activeTab === 'integrity' ? 'active' : ''}`}>
          🛡️ Integrity Alerts
          {integrityCount > 0 && <span className="admin-alert-badge">{integrityCount}</span>}
        </button>
        <button onClick={() => setActiveTab('plans')} className={`admin-tab ${activeTab === 'plans' ? 'active' : ''}`}>⚙️ Plan Config</button>
        <button onClick={() => setActiveTab('pins')} className={`admin-tab ${activeTab === 'pins' ? 'active' : ''}`}>🎫 PIN Management</button>
        <button onClick={() => setActiveTab('reports')} className={`admin-tab ${activeTab === 'reports' ? 'active' : ''}`}>📋 Reports</button>
        <button onClick={() => setActiveTab('audit')} className={`admin-tab ${activeTab === 'audit' ? 'active' : ''}`}>📜 Audit Logs</button>
        <button onClick={() => setActiveTab('admins')} className={`admin-tab ${activeTab === 'admins' ? 'active' : ''}`}>👥 Admins</button>
		<button onClick={() => setActiveTab('security')} className={`admin-tab ${activeTab === 'security' ? 'active' : ''}`}>🔐 Security</button>
      </div>

      {/* ======================================== */}
      {/* OVERVIEW TAB */}
      {/* ======================================== */}
      {activeTab === 'overview' && stats && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <img src="/images/admin/icon-users.png" alt="Users" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/icons/feature-security.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.totalUsers}</span>
                <span className="admin-stat-label">Total Users</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <img src="/images/admin/icon-assets.png" alt="Assets" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/assets/asset-other.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.totalAssets}</span>
                <span className="admin-stat-label">Total Assets</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <img src="/images/admin/icon-trustees.png" alt="Trustees" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/icons/feature-trustees.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.totalTrustees}</span>
                <span className="admin-stat-label">Trustees</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <img src="/images/admin/icon-deceased.png" alt="Deceased" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/symbols/golden-stool.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.deceasedUsers}</span>
                <span className="admin-stat-label">Deceased</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <img src="/images/admin/icon-unclaimed.png" alt="Unclaimed" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/assets/asset-document.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.unclaimedAssets}</span>
                <span className="admin-stat-label">Unclaimed Assets</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <img src="/images/admin/icon-pending.png" alt="Pending" className="admin-stat-icon" onError={(e) => { e.target.src = '/images/icons/feature-trustees.png'; e.target.onerror = null; }} />
              <div className="admin-stat-info">
                <span className="admin-stat-value">{stats.stats.pendingTrusteeInvites}</span>
                <span className="admin-stat-label">Pending Trustees</span>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <h3 className="admin-card-title">Plan Distribution</h3>
            <div className="admin-plan-list">
              {stats.planDistribution.map(plan => (
                <div key={plan._id} className="admin-plan-item">
                  <span className="admin-plan-name">{plan._id || 'free'}</span>
                  <div className="admin-plan-bar">
                    <div className="admin-plan-fill" style={{ width: `${(plan.count / stats.stats.totalUsers) * 100}%` }} />
                  </div>
                  <span className="admin-plan-count">{plan.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-card">
            <h3 className="admin-card-title">📧 Email Export</h3>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
              <button onClick={async () => {
                const token = getToken();
                try {
                  const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/export/newsletter`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'newsletter-subscribers.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  alert('Export failed. Please try again.');
                }
              }} className="admin-export-btn newsletter">📧 Export Newsletter Subscribers</button>
              <button onClick={async () => {
                const token = getToken();
                try {
                  const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/export/all-contacts`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'all-contacts.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  alert('Export failed. Please try again.');
                }
              }} className="admin-export-btn all">📋 Export All Contacts</button>
            </div>
            <small style={{ display: 'block', marginTop: '10px', color: '#666' }}>
              Newsletter subscribers: Users who opted in for marketing emails.
              All contacts: Includes users, Next of Kin, beneficiaries, and trustees.
            </small>
          </div>

          <div className="admin-card">
            <h3 className="admin-card-title">Recent Users</h3>
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Plan</th><th>Joined</th></tr>
                </thead>
                <tbody>
                  {stats.recentActivity?.users?.map(user => (
                    <tr key={user._id}>
                      <td data-label="Name">{user.fullName}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Plan"><span className="admin-plan-badge">{user.plan}</span></td>
                      <td data-label="Joined">{new Date(user.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* USERS TAB */}
      {/* ======================================== */}
      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">All Users</h3>
              <input type="text" placeholder="🔍 Search by name or email..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="admin-search-input" />
            </div>
            {filteredUsers.length === 0 ? (
              <div className="admin-empty-state">
                <img src="/images/dashboard/empty-state.png" alt="No users" className="admin-empty-icon" />
                <p>No users found matching "{searchTerm}"</p>
              </div>
            ) : (
              <>
                <div className="admin-table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Plan</th><th>Assets</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {currentUsers.map(user => (
                        <tr key={user._id}>
                          <td data-label="Name">{user.fullName}</td>
                          <td data-label="Email">{user.email}</td>
                          <td data-label="Plan"><span className="admin-plan-badge">{user.plan}</span></td>
                          <td data-label="Assets">{user.assetCount}</td>
                          <td data-label="Status"><span className={`admin-status-badge ${user.isDeceased ? 'deceased' : 'active'}`}>{user.isDeceased ? 'Deceased' : 'Active'}</span></td>
                          <td data-label="Joined">{new Date(user.createdAt).toLocaleDateString()}</td>
                          <td data-label="Actions"><button className="admin-view-btn" title="View User Details">View</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="admin-pagination">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="admin-page-btn">Previous</button>
                    <span className="admin-page-info">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="admin-page-btn">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* UNCLAIMED ASSETS TAB */}
      {/* ======================================== */}
      {activeTab === 'unclaimed' && (
        <div className="admin-content">
          <div className="admin-card">
            <h3 className="admin-card-title">Unclaimed Assets Registry</h3>
            {unclaimed.length === 0 ? (
              <div className="admin-empty-state">
                <img src="/images/dashboard/empty-state.png" alt="No unclaimed assets" className="admin-empty-icon" />
                <p>No unclaimed assets found.</p>
              </div>
            ) : (
              <div className="admin-table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr><th>Certificate #</th><th>Asset Name</th><th>Type</th><th>Deceased Owner</th><th>Date</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {unclaimed.map(asset => (
                      <tr key={asset._id}>
                        <td data-label="Certificate #"><code className="admin-cert-code">{asset.assetSnapshot?.certificateNumber || asset.certificateNumber || '—'}</code></td>
                        <td data-label="Asset Name">{asset.assetName}</td>
                        <td data-label="Type">{asset.assetType}</td>
                        <td data-label="Deceased Owner">{asset.deceasedName}</td>
                        <td data-label="Date">{new Date(asset.becameUnclaimedAt).toLocaleDateString()}</td>
                        <td data-label="Status"><span className="admin-status-badge unclaimed">{asset.status}</span></td>
                        <td data-label="Actions"><button onClick={() => viewUnclaimedDetails(asset._id)} className="admin-view-btn">Details</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* OTHER TABS */}
      {/* ======================================== */}
      {activeTab === 'integrity' && <IntegrityAlerts />}
      {activeTab === 'reports' && <Reports />}
      {activeTab === 'plans' && <PlanConfig />}
      {activeTab === 'pins' && <PinManagement />}
      {activeTab === 'audit' && <AuditLogs />}
      {activeTab === 'admins' && <AdminManagement />}
	  {activeTab === 'security' && <AdminSecurity />}

      {/* ======================================== */}
      {/* UNCLAIMED ASSET DETAILS MODAL */}
      {/* ======================================== */}
      {showUnclaimedModal && selectedUnclaimed && (
        <div className="modal-overlay" onClick={() => setShowUnclaimedModal(false)}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUnclaimedModal(false)}>×</button>
            <h3 className="modal-title admin-modal-title">Unclaimed Asset Details</h3>
            <div className="admin-modal-body">
              <div className="admin-modal-section">
                <h4>Certificate Information</h4>
                <p><strong>Certificate Number:</strong> <code>{selectedUnclaimed.assetSnapshot?.certificateNumber || selectedUnclaimed.certificateNumber || '—'}</code></p>
                <p><strong>Original Asset ID:</strong> <code>{selectedUnclaimed.originalAssetId}</code></p>
              </div>
              <div className="admin-modal-section">
                <h4>Asset Information</h4>
                <p><strong>Asset Name:</strong> {selectedUnclaimed.assetName}</p>
                <p><strong>Asset Type:</strong> {selectedUnclaimed.assetType?.replace('_', ' ')}</p>
                {selectedUnclaimed.assetSnapshot?.institutionName && <p><strong>Institution:</strong> {selectedUnclaimed.assetSnapshot.institutionName}</p>}
                {selectedUnclaimed.estimatedValue && <p><strong>Estimated Value:</strong> GHS {selectedUnclaimed.estimatedValue.toLocaleString()}</p>}
              </div>
              <div className="admin-modal-section">
                <h4>Deceased Owner</h4>
                <p><strong>Name:</strong> {selectedUnclaimed.deceasedName}</p>
                <p><strong>Date:</strong> {formatDate(selectedUnclaimed.becameUnclaimedAt)}</p>
              </div>
              {selectedUnclaimed.assetSnapshot?.document?.fileUrl && (
                <div className="admin-modal-section">
                  <h4>Supporting Document</h4>
                  <p><strong>File Name:</strong> {selectedUnclaimed.assetSnapshot.document.fileName}</p>
                  <button onClick={() => downloadDocument(selectedUnclaimed.assetSnapshot)} disabled={downloadingDoc} className="admin-doc-btn">{downloadingDoc ? 'Downloading...' : '📎 Download Document'}</button>
                </div>
              )}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowUnclaimedModal(false)} className="modal-cancel-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Inline styles for admin dropdown (add to App.css later) */}
      <style dangerouslySetInnerHTML={{ __html: `
        .admin-profile {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 4px 12px;
          border-radius: 5px;
          background: rgba(255,255,255,0.05);
        }
        .admin-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid #FFD700;
        }
        .admin-name {
          color: #F5F0E6;
          font-size: 14px;
          font-weight: 500;
        }
        .admin-arrow {
          font-size: 10px;
          color: #FFD700;
        }
        .admin-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: #1A1A1A;
          border: 1px solid #FFD700;
          border-radius: 5px;
          min-width: 180px;
          z-index: 100;
          overflow: hidden;
        }
        .admin-dropdown button {
          display: block;
          width: 100%;
          padding: 10px 16px;
          color: #F5F0E6;
          text-decoration: none;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.3s;
        }
        .admin-dropdown button:hover {
          background: #333;
        }
        @media (max-width: 480px) {
          .admin-name { display: none; }
          .admin-avatar { width: 32px; height: 32px; }
          .admin-dropdown { min-width: 160px; right: -10px; }
          .admin-dropdown button { padding: 8px 12px; font-size: 12px; white-space: nowrap; }
        }
      `}} />
    </div>
  );
};

export default AdminDashboard;